import React, { useEffect, useState } from 'react';
import { CloudOff, RefreshCw } from 'lucide-react';
import { getQueuedCount, syncProgressions } from '../lib/offlineQueue';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

export const OfflineIndicator = () => {
  const { t } = useLanguage();
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(getQueuedCount());
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const runSync = async () => {
      if (!navigator.onLine || getQueuedCount() === 0) return;
      setSyncing(true);
      const synced = await syncProgressions();
      setSyncing(false);
      setQueued(getQueuedCount());
      if (synced > 0) toast.success(t('offlineSynced').replace('{count}', synced));
    };

    const goOnline = () => {
      setOnline(true);
      runSync();
    };
    const goOffline = () => setOnline(false);
    const onQueueChanged = (e) => setQueued(e.detail);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('pb-queue-changed', onQueueChanged);
    runSync();

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('pb-queue-changed', onQueueChanged);
    };
  }, [t]);

  if (online && queued === 0) return null;

  return (
    <div
      className="fixed left-1/2 top-3 z-[70] -translate-x-1/2 rounded-full border border-white/10 bg-[#0a0a0a]/95 px-4 py-2 text-xs backdrop-blur-xl"
      data-testid="offline-indicator"
    >
      {!online ? (
        <span className="flex items-center gap-2 text-amber-400">
          <CloudOff className="h-3.5 w-3.5" />
          {t('offlineMode')}
          {queued > 0 && (
            <span className="text-gray-400">
              · {t('offlinePending').replace('{count}', queued)}
            </span>
          )}
        </span>
      ) : (
        <span className="flex items-center gap-2 text-[#32CD32]">
          <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
          {t('offlineSyncing').replace('{count}', queued)}
        </span>
      )}
    </div>
  );
};

export default OfflineIndicator;
